// @ts-expect-error
import { IntercomAPI } from "react-intercom";
import {
  Button,
  ButtonGroup,
  PageContent,
  Spacer,
  Text,
  Flex,
  Panel,
  Alert,
  Title,
  TextAnchor,
  Image,
} from "components";
import pro from "assets/img/Subscription/pro.png";

import {
  CheckoutPurposeEnumType,
  useCreateStripeCheckoutSessionMutation,
  useCreateStripePortalSessionMutation,
  useSubscriptionStatusQuery,
} from "generated/graphql";
import { FC, useEffect, useState } from "react";
import { useBreakpoints, useGetBusinessUnitUserRole } from "hooks";
import { ProFeatures, useSubscriptionPlanFeatures } from "feShared/subscriptions/hooks/useSubscriptionPlanFeatures";

interface SubscriptionContentProps {
  currentBU: number;
  withDeeplinkToMobile?: boolean;
}

export const SubscriptionContent: FC<SubscriptionContentProps> = ({ currentBU, withDeeplinkToMobile }) => {
  const { isAdmin } = useGetBusinessUnitUserRole();
  const { isBreakpointXL, isBreakpointL } = useBreakpoints();
  const [error, setError] = useState<boolean>(false);

  const { isProFeatureEnabledForBU } = useSubscriptionPlanFeatures(currentBU, true);
  const hasAnimalManagementSubscriptionFeature = isProFeatureEnabledForBU(ProFeatures.AnimalManagement);

  const [createStripePortalSession] = useCreateStripePortalSessionMutation();
  const [createCheckoutSession] = useCreateStripeCheckoutSessionMutation();
  const { data: subscriptionStatusData } = useSubscriptionStatusQuery({
    variables: { businessUnitId: currentBU },
    fetchPolicy: "cache-and-network",
  });
  const activationTokenId = subscriptionStatusData?.subscriptionStatus?.activationTokens[0]?.id || undefined;

  useEffect(() => {
    if (error) {
      setError(false);
    }
  }, [currentBU]);

  const handleOpenIntercom = (): void => {
    IntercomAPI("showNewMessage", "I have a question regarding my subscription?\n\n");
  };

  const handleOpenStripe = async () => {
    try {
      if (activationTokenId) {
        const { data: checkoutSessionData } = await createCheckoutSession({
          variables: {
            input: {
              activationTokenId,
              businessUnitId: currentBU,
              returnUrl: window.location.href,
              purpose: CheckoutPurposeEnumType.Activate,
            },
          },
        });

        if (checkoutSessionData && checkoutSessionData?.createStripeCheckoutSession && window.location.href) {
          document.location.href = checkoutSessionData?.createStripeCheckoutSession.sessionUrl;
        }
      } else if (subscriptionStatusData?.subscriptionStatus?.hasStripeSubscriptions) {
        const { data } = await createStripePortalSession({
          variables: {
            input: {
              businessUnitId: currentBU,
              returnUrl: window.location.href,
            },
          },
        });

        if (data && data?.createStripePortalSession && document.location.href) {
          document.location.href = data?.createStripePortalSession.sessionUrl;
        }
      } else {
        const { data: checkoutSessionData } = await createCheckoutSession({
          variables: {
            input: {
              activationTokenId,
              businessUnitId: currentBU,
              returnUrl: window.location.href,
              purpose: CheckoutPurposeEnumType.Pro,
            },
          },
        });

        if (checkoutSessionData && checkoutSessionData?.createStripeCheckoutSession && window.location.href) {
          document.location.href = checkoutSessionData?.createStripeCheckoutSession.sessionUrl;
        }
      }
    } catch (error: unknown) {
      setError(true);
    }
  };

  const supportEmail = "support@breedr.co";

  if (hasAnimalManagementSubscriptionFeature) {
    return (
      <PageContent>
        <Spacer baselineHeight={2} />
        <Flex container containerDirection="row">
          {error && <Alert text="Unable to view subscription details. Please contact Breedr support." color="RED" />}
          <Flex item xs="fill" itemGutter xl={6} itemOrder={isBreakpointXL ? 1 : 2}>
            <Panel>
              <Title secondary>Your Subscription</Title>
              <Spacer baselineHeight={2} />
              <Text>
                Thank you for subscribing to Breedr. Email our customer success team at{" "}
                <a href={`mailto:${supportEmail}`}>support@breedr.co</a> to book your onboarding session. Manage your
                subscription on the manage page.
              </Text>
              <Spacer baselineHeight={2} />

              {!isBreakpointL ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "7.5px" }}>
                  <Button caption="Manage" onClick={handleOpenStripe} disabled={error || !isAdmin} height="medium" />
                  {withDeeplinkToMobile ? (
                    <a
                      style={{ display: "grid" }}
                      href="https://breedr.page.link/?link=https://uk.breedr.co/link/home&apn=com.breedrmobile&isi=1435530409&ibi=co.breedr.breedrapp&efr=1"
                    >
                      <Button caption="Go to mobile app" variant="hollow" colour="grey" height="medium" />
                    </a>
                  ) : (
                    <Button
                      variant="hollow"
                      caption="Get in Contact"
                      colour="grey"
                      onClick={handleOpenIntercom}
                      height="medium"
                    />
                  )}
                </div>
              ) : (
                <ButtonGroup>
                  <Button caption="Manage" onClick={handleOpenStripe} disabled={error || !isAdmin} />
                  {withDeeplinkToMobile ? (
                    <a
                      style={{ marginLeft: "20px" }}
                      href="https://breedr.page.link/?link=https://uk.breedr.co/link/home&apn=com.breedrmobile&isi=1435530409&ibi=co.breedr.breedrapp&efr=1"
                    >
                      <Button caption="Go to mobile app" variant="hollow" colour="grey" />
                    </a>
                  ) : (
                    <Button variant="hollow" caption="Get in Contact" colour="grey" onClick={handleOpenIntercom} />
                  )}
                </ButtonGroup>
              )}
            </Panel>
          </Flex>
          <Flex item xs="fill" xl={6} itemGutter itemOrder={isBreakpointXL ? 2 : 1}>
            <Image src={pro} />
            {!isBreakpointXL && <Spacer baselineHeight={2} />}
          </Flex>
        </Flex>
      </PageContent>
    );
  }

  if (!isBreakpointL) {
    return (
      <PageContent>
        <Spacer baselineHeight={2} />
        <Flex container containerDirection="row">
          {error && <Alert text="Unable to upgrade subscription. Please contact Breedr support." color="RED" />}
          <Flex item xs="fill" itemGutter xl={6}>
            <Spacer baselineHeight={2} />
            <Panel>
              <Title secondary>Upgrade to Pro</Title>
              <Spacer baselineHeight={2} />
              <Text>
                Breedr Pro is priced to support British farmers and you can get all the benefits of Pro for FREE when
                you buy or sell 20 animals.
              </Text>

              <Spacer baselineHeight={2} />
              <Image src={pro} style={{ borderRadius: "4px" }} />
              <Spacer baselineHeight={2} />

              <div style={{ display: "flex", flexDirection: "column" }}>
                <TextAnchor
                  caption="Find out more"
                  href="https://www.breedr.co/features-and-pricing/"
                  style={{ alignSelf: "center", color: "#1054A7" }}
                />

                <Spacer baselineHeight={2} />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "7.5px" }}>
                  <Button caption="Upgrade" onClick={handleOpenStripe} disabled={error || !isAdmin} height="medium" />
                  {withDeeplinkToMobile ? (
                    <a
                      style={{ display: "contents" }}
                      href="https://breedr.page.link/?link=https://uk.breedr.co/link/home&apn=com.breedrmobile&isi=1435530409&ibi=co.breedr.breedrapp&efr=1"
                    >
                      <Button caption="Go to mobile app" height="medium" variant="hollow" colour="grey" />
                    </a>
                  ) : (
                    <Button
                      variant="hollow"
                      caption="Get in Contact"
                      colour="grey"
                      onClick={handleOpenIntercom}
                      height="medium"
                    />
                  )}
                </div>
                <Spacer baselineHeight={2} />
                <Text style={{ textAlign: "center" }} smallest>
                  12 months free when you trade livestock with Breedr or £29.50+VAT a month.
                </Text>
                <Spacer baselineHeight={2} />
              </div>

              <Spacer baselineHeight={1} />
              <Text>Included:</Text>
              <Spacer baselineHeight={1} />
              <Text>
                <strong>Multi-Species</strong> management for your sheep and cattle
              </Text>
              <Spacer baselineHeight={1} />
              <Text>
                <strong>Quick Weighing</strong> weigh with ease with crush mode
              </Text>
              <Spacer baselineHeight={1} />
              <Text>
                <strong>Complete Compliance</strong> manage movement and medicines
              </Text>
              <Spacer baselineHeight={1} />
              <Text>
                <strong>Access Support</strong> 30 minute onboarding session and phone support
              </Text>
              <Spacer baselineHeight={1} />
              <Text>
                <strong>Advanced Insights</strong> with our state of the art reporting tools
              </Text>
              <Spacer baselineHeight={1} />
              <Text>
                <strong>Breeding</strong> maximise your herd performance in the app
              </Text>
              <Spacer baselineHeight={2} />
              <Text>
                Our individual animal analytics helps you discover key areas for improvement in DLWG, breeding &amp;
                health, and you can track your progress in our easy &amp; time-saving app.
              </Text>
            </Panel>
          </Flex>
        </Flex>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Spacer baselineHeight={2} />
      <Flex container containerDirection="row">
        {error && <Alert text="Unable to upgrade subscription. Please contact Breedr support." color="RED" />}
        <Flex item xs="fill" itemGutter l={6} itemOrder={isBreakpointL ? 1 : 2}>
          <Panel>
            <Title secondary>Upgrade to Pro</Title>

            <Spacer baselineHeight={2} />
            <Text>
              Breedr Pro is priced to support British farmers and you can get all the benefits of Pro for FREE when you
              buy or sell 20 animals.
            </Text>
            <Spacer baselineHeight={1} />
            <Text>Included:</Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Multi-Species</strong> management for your sheep and cattle
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Quick Weighing</strong> weigh with ease with crush mode
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Complete Compliance</strong> manage movement and medicines
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Access Support</strong> 30 minute onboarding session and phone support
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Advanced Insights</strong> with our state of the art reporting tools
            </Text>
            <Spacer baselineHeight={1} />
            <Text>
              <strong>Breeding</strong> maximise your herd performance in the app
            </Text>
            <Spacer baselineHeight={2} />
            <Text>
              Our individual animal analytics helps you discover key areas for improvement in DLWG, breeding &amp;
              health, and you can track your progress in our easy &amp; time-saving app.
            </Text>
            <Spacer baselineHeight={2} />
            <Text>12 months free when you trade livestock with Breedr or £29.50+VAT a month.</Text>
            <Spacer baselineHeight={2} />
            <TextAnchor caption="Find out more" target="_blank" href="https://www.breedr.co/features-and-pricing/" />

            <ButtonGroup>
              <Button caption="Upgrade" onClick={handleOpenStripe} disabled={error || !isAdmin} />
              <Button variant="hollow" caption="Get in Contact" colour="grey" onClick={handleOpenIntercom} />
            </ButtonGroup>
          </Panel>
        </Flex>
        <Flex item xs="fill" l={6} itemGutter itemOrder={isBreakpointL ? 2 : 1}>
          <Image src={pro} />
          {!isBreakpointXL && <Spacer baselineHeight={2} />}
        </Flex>
      </Flex>
    </PageContent>
  );
};
